import { useCallback } from 'react'

import { Box, Stack, Text } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

import NoSearchResults from './NoSearchResults'
import SearchOption from './SearchOption'
import SearchOptions from './SearchOptions'
import SearchSelect from './SearchSelect'
import SearchSelectedTags from './SearchSelectedTags'
import {
  clearStaffOptions,
  loadStaffOptions,
  paginateStaffOptions,
} from '../../../redux/features/staffSlice'
import { convertDistrictToNumeric, hasPermission } from '../../../utils'
import ActiveStaffIndicator from '../ActiveStaffIndicator'

const StaffSelect = ({
  onSelect = () => {},
  onRemove = () => {},
  onClear = () => {},
  showDistrictLabel = false,
  district,
  districtOnly,
  initialSelected,
  inputProps = {},
  showUnassigned,
  showActiveIndicator = false,
}) => {
  const dispatch = useDispatch()

  const staffOptions = useSelector(state => state.staff.options) || []
  const searchLoading = useSelector(state => state.staff.options_loading)
  const user = useSelector(state => state.auth.user)

  const queryParams = {
    value: '',
    districtOnly: districtOnly,
    district: district,
  }

  if (showActiveIndicator) queryParams.show_inactive = [true]

  const loadOptions = searchTerm => {
    dispatch(clearStaffOptions())
    dispatch(loadStaffOptions({ ...queryParams, value: searchTerm }))
  }
  const debouncedHandleChange = useDebouncedCallback(e => {
    loadOptions(e.target.value)
  }, 200)

  const loadMoreResults = useCallback(() => {
    dispatch(paginateStaffOptions())
  }, [])

  const filterOptions = useCallback(
    (searchTerm, selectedOptions) => {
      let unselectedOptions = staffOptions.filter(
        ({ id }) =>
          !selectedOptions.find(
            ({ value }) =>
              value.id === id ||
              convertDistrictToNumeric(value.district) === searchTerm
          )
      )

      if (searchTerm)
        unselectedOptions = unselectedOptions.filter(
          ({ full_name, district }) =>
            full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            convertDistrictToNumeric(district) === searchTerm
        )
      if (
        unselectedOptions.length === 0 ||
        (unselectedOptions.length === 1 && unselectedOptions[0].id === '-1')
      )
        return null

      return unselectedOptions
    },
    [staffOptions]
  )

  const showDistrictHelper =
    hasPermission(user, 'casework.assign_any_staff') || showDistrictLabel

  return (
    <SearchSelect>
      <SearchOptions
        loadOptions={loadOptions}
        loadMoreOptions={loadMoreResults}
        onClear={onClear}
        onSearch={debouncedHandleChange}
        isLoading={searchLoading}
        initialSelected={initialSelected}
        inputProps={inputProps}
      >
        {({ searchTerm, selectedOptions }) =>
          filterOptions(searchTerm, selectedOptions)?.map((staff, index) => {
            return (
              (staff.id !== '-1' || (staff.id === '-1' && showUnassigned)) && (
                <SearchOption
                  key={`staff-option-${index}`}
                  option={{ label: staff.full_name, value: staff }}
                  onSelect={onSelect}
                >
                  <Stack gap={0}>
                    <Box align='left'>
                      {showActiveIndicator && (
                        <ActiveStaffIndicator active={staff.is_active} />
                      )}
                      {staff.full_name}
                    </Box>
                    {(staff.role || staff.district) && (
                      <Text align='left' fontSize='sm' color='gray.500'>
                        {staff.role}
                        {showDistrictHelper &&
                          ` (District ${convertDistrictToNumeric(
                            staff.district
                          )})`}
                      </Text>
                    )}
                  </Stack>
                </SearchOption>
              )
            )
          }) || <NoSearchResults> No staff found </NoSearchResults>
        }
      </SearchOptions>
      <SearchSelectedTags onRemove={onRemove} />
    </SearchSelect>
  )
}

export default StaffSelect
